import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { jwtDecode } from "jwt-decode";

import API_ENDPOINTS from "../../api/apiEndpoints";

export const AuthContext = React.createContext(null);

/**
 * Provides authentication and authorization context to the app.
 * @memberof module:providers
 * @function
 * @name AuthProvider
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components
 * @returns {JSX.Element}
 */
export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // --- Helpers ---
  const loadStorage = (key, fallback = null, parseJson = false) => {
    const value = localStorage.getItem(key);
    if (!value) return fallback;
    return parseJson ? JSON.parse(value) : value;
  };

  const saveStorage = (key, value) => {
    if (value === null || value === undefined) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(
        key,
        typeof value === "boolean" ? JSON.stringify(value) : value
      );
    }
  };

  const [general_access_token, setGeneralAccessToken] = useState(() =>
    loadStorage("general_access_token")
  );
  const [general_refresh_token, setGeneralRefreshToken] = useState(() =>
    loadStorage("general_refresh_token")
  );
  const [tenant_access_token, setTenantAccessToken] = useState(() =>
    loadStorage("tenant_access_token")
  );
  const [tenant_refresh_token, setTenantRefreshToken] = useState(() =>
    loadStorage("tenant_refresh_token")
  );
  const [selected_tenant_id, setSelectedTenantId] = useState(() =>
    loadStorage("selected_tenant_id")
  );

  const [userId, setUserId] = useState(() => loadStorage("userId"));

  const [isUserAuthenticated, setIsUserAuthenticated] = useState(() =>
    loadStorage("is_authenticated", false, true)
  );
  const [isUserAuthorized, setIsUserAuthorized] = useState(() =>
    loadStorage("is_authorized", false, true)
  );

  useEffect(() => {
    // Store the token in localStorage when it changes
    var access_token = localStorage.getItem("general_access_token");
    var refresh_token = localStorage.getItem("general_refresh_token");
    if (!access_token || !refresh_token) {
      if (
        location.pathname === "/register/owner" ||
        location.pathname === "/register/user" ||
        location.pathname === "/forgotpassword" ||
        location.pathname === "/changepassword"
      ) {
        navigate(location.pathname);
      } else navigate("/login");
    } else {
      const origin = location.pathname || "/overview/dashboard";
      navigate(origin);
    }
  }, []);

  /**
   * Verifies if the current user has any of the specified roles.
   * @param {string[] | null} roles - Required roles
   * @returns {boolean} True if the user has at least one role
   */
  const verifyAuthorization = (roles) => {
    if (roles === null || roles?.length === 0) return true;

    if (!general_access_token || !tenant_access_token) return;

    const { groups = [], tenant_role = "" } = jwtDecode(tenant_access_token);
    const authorities = [...groups.map((group) => group.roleName), tenant_role];

    return authorities?.some((element) => new Set(roles).has(element));
  };

  /**
   * Retrieves the roles of the user for a given group name.
   * @param {string} groupName
   * @returns {string[]} List of roles
   */
  const getAuthorizationByGroup = (groupName) => {
    if (!general_access_token) return;

    const { groups = [], tenant_role = "" } = jwtDecode(tenant_access_token);
    const authorities = [
      ...groups
        .filter((group) => group.groupName === groupName)
        .map((group) => group.roleName),
      tenant_role,
    ];

    return authorities;
  };

  /**
   * Retrieves group info matching any of the provided role names.
   * @param {string[]} roleNames
   * @returns {Array} List of groups matching the role names
   */
  const getAuthorizationByRoles = (roleNames) => {
    if (!tenant_access_token) return [];

    const { groups = [] } = jwtDecode(tenant_access_token);

    return groups.filter((group) => roleNames.includes(group.roleName));
  };

  /**
   * Refreshes the general access token using the refresh token.
   * @async
   * @returns {Promise<Object|undefined>} New tenant tokens or undefined on failure
   */
  const handleRefreshToken = async () => {
    const response = await fetch(
      `${API_ENDPOINTS.usersApi.baseUrl}/oauth2/token`,
      {
        method: "POST",
        withCredentials: true, //allows sending session cookie
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Authorization: "Basic ZGVtby1jbGllbnQ6ZGVtby1zZWNyZXQ=",
        },
        body: new URLSearchParams({
          refresh_token: general_refresh_token,
          grant_type: "refresh_token",
        }),
      }
    );
    const data = await response.json();
    if (data.access_token && data.refresh_token) {
      setGeneralAccessToken(data.access_token);
      setGeneralRefreshToken(data.refresh_token);
      localStorage.setItem("general_access_token", data.access_token);
      localStorage.setItem("general_refresh_token", data.refresh_token);
      if (isUserAuthorized) {
        const tenant_auth = await handleAuthorization(
          selected_tenant_id,
          data.access_token
        );
        return tenant_auth;
      }
    } else {
      handleLogout();
    }
  };

  /**
   * Handles login and sets general access tokens.
   * @async
   * @param {string} username
   * @param {string} password
   * @returns {Promise<boolean>} True if login succeeded
   */
  const handleAuthentication = async (username, password) => {
    const response = await fetch(
      `${API_ENDPOINTS.usersApi.baseUrl}/oauth2/token`,
      {
        method: "POST",
        //mode: 'no-cors',
        withCredentials: true, //allows sending session cookie
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Authorization: "Basic ZGVtby1jbGllbnQ6ZGVtby1zZWNyZXQ=",
        },
        body: new URLSearchParams({
          username: username,
          password: password,
          grant_type: "grant_password",
          scope: "general_access",
        }),
      }
    );
    const data = await response.json();

    if (data.access_token && data.refresh_token) {
      setGeneralAccessToken(data.access_token);
      setGeneralRefreshToken(data.refresh_token);
      setIsUserAuthenticated(true);
      localStorage.setItem("general_access_token", data.access_token);
      localStorage.setItem("general_refresh_token", data.refresh_token);
      localStorage.setItem("is_authenticated", JSON.stringify(true));
      return true;
    } else {
      handleLogout();
      return false;
    }
  };

  /**
   * Handles tenant-specific authorization using the general token.
   * @async
   * @param {string} g_general_access_token
   * @param {string} tenant_id
   * @returns {Promise<Object|undefined>} Token object or undefined on failure
   */
  const handleAuthorization = async (tenant_id, accessTokenOverride) => {
    const token = accessTokenOverride ?? general_access_token;
    const response = await fetch(
      `${API_ENDPOINTS.usersApi.baseUrl}/oauth2/token`,
      {
        method: "POST",
        //mode: 'no-cors',
        withCredentials: true, //allows sending session cookie
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Authorization: "Basic ZGVtby1jbGllbnQ6ZGVtby1zZWNyZXQ=",
        },
        body: new URLSearchParams({
          access_token: token,
          tenantId: tenant_id,
          grant_type: "grant_tenant",
        }),
      }
    );
    const data = await response.json();

    if (data.access_token && data.refresh_token) {
      setTenantAccessToken(data.access_token);
      setTenantRefreshToken(data.refresh_token);
      setSelectedTenantId(tenant_id);
      setIsUserAuthorized(true);
      setUserId(jwtDecode(data.access_token).userId);

      localStorage.setItem("tenant_access_token", data.access_token);
      localStorage.setItem("tenant_refresh_token", data.refresh_token);
      localStorage.setItem("selected_tenant_id", tenant_id);
      localStorage.setItem("is_authorized", JSON.stringify(true));
      localStorage.setItem("userId", jwtDecode(data.access_token).userId);
      return data;
    } else {
      handleTenantLogout();
    }
  };

  /**
   * Clears all auth state and local storage values.
   */
  const handleLogout = () => {
    setGeneralAccessToken(null);
    setGeneralRefreshToken(null);
    setTenantAccessToken(null);
    setTenantRefreshToken(null);
    setIsUserAuthenticated(false);
    setIsUserAuthorized(false);
    localStorage.clear();
  };

  const handleTenantLogout = () => {
    setTenantAccessToken(null);
    setTenantRefreshToken(null);
    setIsUserAuthorized(false);
    setSelectedTenantId(null);
    localStorage.removeItem("tenant_access_token");
    localStorage.removeItem("tenant_refresh_token");
    localStorage.removeItem("selected_tenant_id");
    localStorage.removeItem("is_authorized");

    localStorage.removeItem("userId");
  };

  const value = {
    general_access_token: general_access_token,
    general_refresh_token: general_refresh_token,
    tenant_access_token: tenant_access_token,
    tenant_refresh_token: tenant_refresh_token,
    selected_tenant_id: selected_tenant_id,
    userId: userId,
    isUserAuthorized: isUserAuthorized,
    verifyAuthorization: verifyAuthorization,
    getAuthorizationByGroup: getAuthorizationByGroup,
    getAuthorizationByRoles: getAuthorizationByRoles,
    isUserAuthenticated: isUserAuthenticated,
    onRefreshToken: handleRefreshToken,
    onLogin: handleAuthentication,
    onTenant: handleAuthorization,
    onTenantLogout: handleTenantLogout,
    onLogout: handleLogout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
